import {
  CmBaseTable,
  CmBox,
  CmButton,
  CmHeading,
  CmList,
  CmListItem,
  CmModalBody,
  CmModalFooter,
  CmTableBodyRow,
  CmTd,
  CmText,
  CmTooltip,
} from '@/components/uiParts';
import { orderMethod } from '@/domain/models/Order/orderMethod';
import { CurrencyDomainService, DateDomainService } from '@/domain/services';
import { nonNullableStr, ommisionText } from '@/lib/util';
import { itemTableThs } from '@/pages/menu-managements/items/service';
import { UberPreviewBulkUpsertBrandItemsResponse } from '@/services/BrandItem/hooks/upload/useMenuManagementUberPreviewBulkUpsertBrandItems';

type Props = {
  masterStoreName: string;
  insertCount: UberPreviewBulkUpsertBrandItemsResponse['insert_count'];
  updateCount: UberPreviewBulkUpsertBrandItemsResponse['update_count'];
  errorsByLine: UberPreviewBulkUpsertBrandItemsResponse['errors_by_line'];
  brandItems: UberPreviewBulkUpsertBrandItemsResponse['brand_items'];
  brandCategories: UberPreviewBulkUpsertBrandItemsResponse['brand_categories'];
  brandOptionGroups: UberPreviewBulkUpsertBrandItemsResponse['brand_option_groups'];
  uberCategoryIdsByUberMenuItemId: UberPreviewBulkUpsertBrandItemsResponse['uber_category_ids_by_uber_menu_item_id'];
  uberModifierGroupIdsByUberMenuItemId: UberPreviewBulkUpsertBrandItemsResponse['uber_modifier_group_ids_by_uber_menu_item_id'];
  onClickCancel: () => void;
  isImportDisabled: boolean;
  isImportLoading: boolean;
  onClickImport: () => void;
};

const TABLE_THS = itemTableThs.filter((e) => e.label !== 'アクション');

export const PreviewBrandItems: React.FC<Props> = (props) => {
  const errorLines = Object.keys(props.errorsByLine);
  const hasError = errorLines.length > 0;
  const brandCategoryByUberCategoryId = props.brandCategories.reduce(
    (acc, cur) => {
      if (cur.uber_category_id === null) return acc;
      acc[cur.uber_category_id] = cur;
      return acc;
    },
    {} as Record<string, typeof props.brandCategories[0]>
  );
  const brandOptionGroupByUberModifierGroupId = props.brandOptionGroups.reduce(
    (acc, cur) => {
      if (cur.uber_modifier_group_id === null) return acc;
      acc[cur.uber_modifier_group_id] = cur;
      return acc;
    },
    {} as Record<string, typeof props.brandOptionGroups[0]>
  );

  const getCategoryName = (uberCategoryId: string) => {
    const category = brandCategoryByUberCategoryId[uberCategoryId];
    return category?.title?.translation?.ja || '';
  };

  const getOptionGroupName = (uberModifierGroupId: string) => {
    const optionGroup =
      brandOptionGroupByUberModifierGroupId[uberModifierGroupId];
    return optionGroup?.title?.translation?.ja || '';
  };

  return (
    <>
      <CmModalBody
        display="flex"
        flexDirection="column"
        paddingTop={0}
        minHeight={500}
      >
        <CmBox>
          <CmHeading size="sm" marginBottom="4">
            対象店舗：{props.masterStoreName}
          </CmHeading>
          <CmList>
            <CmListItem>
              <CmText>追加：{props.insertCount}件</CmText>
            </CmListItem>
            <CmListItem>
              <CmText>更新：{props.updateCount}件</CmText>
            </CmListItem>
          </CmList>
        </CmBox>
        {hasError && (
          <CmBox marginTop={5}>
            <CmText fontWeight="bold">
              エラー行：
              {errorLines
                .map(
                  (line) =>
                    `${line}: （${props.errorsByLine[line].join(', ')}）`
                )
                .join(', ')}
            </CmText>
            <CmText fontSize="small">※エラー行はインポートされません</CmText>
          </CmBox>
        )}
        <CmBox marginTop={5}>
          <CmBaseTable ths={TABLE_THS}>
            {props.brandItems.map((item, i) => {
              const uberCategoryIds =
                props.uberCategoryIdsByUberMenuItemId[
                  item.uber_menu_item_id || ''
                ] || [];
              const categoryNames = uberCategoryIds
                .map(getCategoryName)
                .filter((name) => name.length > 0)
                .join(', ');
              const uberModifierGroupIds =
                props.uberModifierGroupIdsByUberMenuItemId[
                  item.uber_menu_item_id || ''
                ] || [];
              const optionGroupNames = uberModifierGroupIds
                .map(getOptionGroupName)
                .filter((name) => name.length > 0)
                .join(', ');

              return (
                // NOTE: いずれかの行を選択してインポートする等の追加実装がありそうなので、PreviewCsvのテーブルとの共通化はしない
                <CmTableBodyRow key={i + 1}>
                  <CmTd>{item.title.translation.ja}</CmTd>
                  <CmTd>
                    {nonNullableStr(
                      item.order_methods.map((e) => orderMethod[e]).join(', ')
                    )}
                  </CmTd>
                  <CmTd>
                    {item.image_url !== null ? '設定あり' : '設定なし'}
                  </CmTd>
                  <CmTd>
                    {item.takeout !== null &&
                      CurrencyDomainService.priceFormat(item.takeout.price)}
                  </CmTd>
                  <CmTd>
                    {item.eat_in !== null &&
                      CurrencyDomainService.priceFormat(item.eat_in.price)}
                  </CmTd>
                  <CmTd>
                    {item.delivery !== null &&
                      CurrencyDomainService.priceFormat(item.delivery.price)}
                  </CmTd>
                  <CmTd>
                    <CmTooltip label={nonNullableStr(categoryNames)}>
                      {ommisionText(nonNullableStr(categoryNames), 20)}
                    </CmTooltip>
                  </CmTd>
                  <CmTd>
                    <CmTooltip label={nonNullableStr(optionGroupNames)}>
                      {ommisionText(nonNullableStr(optionGroupNames), 20)}
                    </CmTooltip>
                  </CmTd>
                  <CmTd>
                    <CmTooltip label={item.memo ?? ''}>
                      {ommisionText(item.memo ?? '', 20)}
                    </CmTooltip>
                  </CmTd>
                  <CmTd textAlign="center">
                    <CmTooltip label={item.updated_at}>
                      {DateDomainService.datetimeFormat(
                        new Date(item.updated_at)
                      )}
                    </CmTooltip>
                  </CmTd>
                </CmTableBodyRow>
              );
            })}
          </CmBaseTable>
        </CmBox>
      </CmModalBody>
      <CmModalFooter display="flex" alignItems="center">
        <CmButton variant="outline" mr={3} onClick={props.onClickCancel}>
          キャンセル
        </CmButton>
        <CmButton
          disabled={props.isImportDisabled}
          isLoading={props.isImportLoading}
          onClick={props.onClickImport}
        >
          商品をインポートする
        </CmButton>
      </CmModalFooter>
    </>
  );
};
